// Application root: composes the two state lanes (navigation, account) and
// picks the shell for the current route. The lanes themselves live under
// src/app/:
//   routing/  — URL parsing + route state + navigate (useAppNavigation)
//   account/  — api.ato.run cookie session (useAccountController)
//   routes/   — the per-shell route → screen switches
import { lazy, useEffect } from "react";
import { t, useLocale } from "./i18n/locale";
import AccountShell from "./components/AccountShell";
import DeferredView from "./components/DeferredView";
import LoadingView from "./components/LoadingView";
import AuthRecoveryDialog from "./components/AuthRecoveryDialog";
import CloudWaitlistDeepLink from "./components/CloudWaitlistDeepLink";
import ErrorBanner from "./components/ErrorBanner";
import { useAppNavigation } from "./app/routing/useAppNavigation";
import {
  isPersonalAppsSurface,
  isMainBleedRoute,
} from "./app/routing/routeChrome";
import { useAccountController } from "./app/account/useAccountController";
import StandaloneRoutes, {
  isStandaloneRoute,
} from "./app/routes/StandaloneRoutes";
import AccountRoutes from "./app/routes/AccountRoutes";

const AppDetailsModal = lazy(
  () => import("./components/personal-apps/AppDetailsModal"),
);

const SITE_TITLE = "ato.run — Discover what’s open";

/** The tab/history identity of the route. null leaves an intentional title alone. */
function routeTitle(route: string, homeView: string): string | null {
  if (route === "/")
    return homeView === "my-apps" ? `${t("Library")} — ato.run` : SITE_TITLE;
  if (route === "/studio" || route === "/creator")
    return `${t("Studio")} — ato.run`;
  if (route === "/apps/new") return `${t("Create new")} — ato.run`;
  if (route === "/sign-in") return `${t("Sign in")} — ato.run`;
  if (route === "/settings" || route.startsWith("/settings/"))
    return `${t("Settings")} — ato.run`;
  if (route === "/runners") return `${t("Runners")} — ato.run`;
  return null;
}

export default function App() {
  const locale = useLocale();
  const navigation = useAppNavigation();
  const { route, navigate } = navigation;
  const account = useAccountController();

  // Multi-tab, history and bookmarks need to tell the screens apart; a single
  // static title made every ato.run tab look like Discover.
  useEffect(() => {
    const next = routeTitle(route, navigation.homeView);
    if (next) document.title = next;
  }, [route, navigation.homeView, locale]);

  // Full-screen routes outside both shells (callbacks, landing, sign-in,
  // billing) — always available, in any mode.
  if (isStandaloneRoute(route)) {
    return (
      <StandaloneRoutes
        route={route}
        navigate={navigate}
        applyAccountState={account.applyAccountState}
        commitSignedInSession={account.commitSignedInSession}
      />
    );
  }

  const personalAppsSurface = isPersonalAppsSurface(route);

  return (
    <>
      <AccountShell
        route={route}
        discoveryState={navigation.discoveryState}
        homeView={navigation.homeView}
        onNavigate={navigate}
        user={
          account.accountState.status === "signed_in"
            ? account.accountState.session.user
            : null
        }
        // Personal Apps and Studio are workspaces: their screens own the whole
        // main column, so the marketing footer is not repeated under them.
        hideFooter={
          personalAppsSurface ||
          route === "/studio" ||
          route === "/creator"
        }
        onSignIn={account.handleAccountSignIn}
        onSignOut={account.handleAccountSignOut}
        mainBleed={isMainBleedRoute(route)}
      >
        {account.signInError && (
          <ErrorBanner
            message={account.signInError}
            onDismiss={account.dismissSignInError}
          />
        )}
        <DeferredView key={route}>
          <AccountRoutes
            route={route}
            navigate={navigate}
            addSource={navigation.addSource}
            homeView={navigation.homeView}
            discoveryState={navigation.discoveryState}
            account={account}
          />
        </DeferredView>
      </AccountShell>
      {navigation.detailState ? (
        <DeferredView
          key={`${navigation.detailState.target.kind}:${navigation.detailState.target.id}`}
          fallback={<LoadingView label="Loading app details…" />}
        >
          <AppDetailsModal
            navigation={navigation}
            accountState={account.accountState}
            onSignIn={account.handleAccountSignIn}
          />
        </DeferredView>
      ) : null}
      <AuthRecoveryDialog />
      <CloudWaitlistDeepLink />
    </>
  );
}
